// What « Tout approuver » will really do — said before the click (2026-09-29).
//
// 28 Sept, 23:23 UTC: one click approved 70 follow-ups; 66 had waited more than
// 48 h, the oldest since 18 August. Owner: « A queued message older than 48h is
// dropped, not sent. Before approving a batch, show how many are stale. » The
// worker decides (`stale` on each row, worker/src/lib/followUpQueueAge.ts) and
// drops them at approval; this only counts and says it — and, at night, that
// what goes will go at 08:00 (worker/src/lib/nightHold.ts).
//
// Guard: ./followUpQueuePlan.test.ts
import type { TFunc } from '@/lib/i18n';

export interface QueuePlanRow { stale?: boolean; reachable?: boolean | null }
export interface ApprovalPlan { total: number; stale: number; willSend: number; unreachable: number }
export interface QueueNight { active: boolean; sends_at: string | null }

/** A stale row is dropped whatever else is true; of the rest, an unreachable one does not go. */
export function approvalPlan(rows: QueuePlanRow[]): ApprovalPlan {
  const stale = rows.filter((r) => r.stale).length;
  const fresh = rows.filter((r) => !r.stale);
  const unreachable = fresh.filter((r) => r.reachable === false).length;
  return { total: rows.length, stale, willSend: fresh.length - unreachable, unreachable };
}

// French is the key (lib/i18n.tsx); every one of these has an English line in
// lib/i18n/en/assistant.ts — the test holds them together.
const S = {
  all: 'Tout approuver',
  allUnreachable: 'Tout approuver · {s} partiront, {u} non',
  allStale: 'Tout approuver · {s} partiront, {x} trop anciennes',
  allBoth: 'Tout approuver · {s} partiront, {u} non, {x} trop anciennes',
  ask: 'Approuver les {n} relance(s) « {r} » ?',
  willSend: '{s} partiront.',
  staleLine: '{x} ont plus de 48 h dans la file : elles seront abandonnées, jamais envoyées.',
  unreachableLine: '{u} ne partiront PAS : ces numéros ne sont pas dans la liste de test.',
  nightLine: 'Il fait nuit au Maroc : celles qui partent attendront 8 h (heure du Maroc).',
  rowStale: 'Plus de 48 h dans la file — ne partira pas',
  rowDrop: 'Retirer (plus de 48 h)',
  nightBanner: 'Il fait nuit au Maroc : ce que vous approuvez maintenant partira à 8 h.',
} as const;

export const QUEUE_STRINGS: readonly string[] = Object.values(S);
export const QUEUE_TEXT = S;

export function approveAllLabel(t: TFunc, p: ApprovalPlan): string {
  const vars = { s: p.willSend, u: p.unreachable, x: p.stale };
  if (p.stale && p.unreachable) return t(S.allBoth, vars);
  if (p.stale) return t(S.allStale, vars);
  if (p.unreachable) return t(S.allUnreachable, vars);
  return t(S.all);
}

export function approveAllConfirm(t: TFunc, ruleName: string, p: ApprovalPlan, night: QueueNight | null | undefined): string {
  const vars = { n: p.total, r: ruleName, s: p.willSend, u: p.unreachable, x: p.stale };
  const lines = [t(S.ask, vars), '', t(S.willSend, vars)];
  if (p.stale) lines.push(t(S.staleLine, vars));
  if (p.unreachable) lines.push(t(S.unreachableLine, vars));
  if (night?.active && p.willSend > 0) lines.push('', t(S.nightLine, vars));
  return lines.join('\n');
}
